import { Auth } from 'aws-amplify';
import { isAdmin, isMultipleOrganization } from '../../Utils/ServerUtil';
import { getCurrentOrganizationUrlParam } from '../../Utils/helpers'
import config from '../../config/runtime';
import { newRequestId, requestStart, requestSuccess, requestFailure } from '../../config/logger';

/**
 * Base API service for making requests to the server
 * Handles authentication and common request methods
 */
class ApiService {
  /**
   * Get the base URL for API requests
   * @param {string} version - API version (v1 or v3)
   * @returns {string} - Base URL for API requests
   */
  getBaseUrl(version = 'v3') {
    return version === 'v3'
      ? config.API_ENDPOINT_V3
      : config.API_ENDPOINT;
  }

  /**
   * Set the current organization ID
   * @param {string} organizationId - The organization ID to set
   */
  setOrganization(organizationId) {
    if (organizationId) {
      // Store the organization ID in localStorage (encoded like the original code)
      localStorage['hw.' + window.btoa('organization')] = window.btoa(organizationId);
    } else {
      // Remove organization from localStorage
      delete localStorage['hw.' + window.btoa('organization')];
    }
  }

  /**
   * Get the current organization ID
   * @returns {string|null} - Current organization ID
   */
  getCurrentOrganization() {
    const organization = localStorage['hw.' + window.btoa('organization')];
    return organization ? window.atob(organization) : 'f376c401-6295-4038-a146-e8b52eef6385';
  }

  /**
   * Get authentication headers for API requests
   * @returns {Promise<Object>} - Headers object with authentication
   */
  async getAuthHeaders() {
    let jwtToken;
    let tokenRefreshError = false;

    try {
      const session = await Auth.currentSession();
      jwtToken = session.idToken.jwtToken;
    } catch (e) {
      try {
        const session = await Auth.currentSession();
        jwtToken = session.idToken.jwtToken;
      } catch (e) {
        console.error('Error getting authentication token:', e);
        tokenRefreshError = true;
        // Allow to go through without jwtToken for server requests not requiring an authenticated user
      }
    }

    const headers = {
      Authorization: jwtToken, // Note: Capital 'A' to match original
      'content-type': 'application/json',
      Accept: 'application/json',
    };

    // Add organization header if needed - matching original logic exactly
    if (isMultipleOrganization()) {
      const organization = this.getCurrentOrganization();
      console.log('Organization', organization);
      headers['HW-Organization'] = organization;
      console.log('Headers', headers);
    }

    return headers;
  }

  /**
   * Make a request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @param {string} version - API version (v1 or v3)
   * @returns {Promise<Object>} - Response data
   */
  async request(endpoint, options = {}, version = 'v3') {
    const headers = await this.getAuthHeaders();
    const url = `${this.getBaseUrl(version)}${endpoint}`;
    const requestId = newRequestId();

    const requestOptions = {
      method: options.method || 'GET',
      mode: 'cors',
      headers: { ...headers, 'X-Request-ID': requestId, ...options.headers },
      ...(options.body && { body: JSON.stringify(options.body) }),
    };

    try {
      const startTime = Date.now();
      requestStart(requestId, {
        method: requestOptions.method,
        endpoint,
        base_url: this.getBaseUrl(version),
        orgId: headers['HW-Organization']
      });
      const response = await fetch(url, requestOptions);

      // console.log(response)
      if (!response.ok) {
        requestFailure(requestId, {
          method: requestOptions.method,
          endpoint,
          status: response.status
        });
        throw new Error(`API request failed with status: ${response.status}`);
      }

      if (response.status === 204) {
        return null;
      }

      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();

      if (!text) {
        return null;
      }

      if (contentType.includes('application/json')) {
        return JSON.parse(text);
      }

      return text;
    } catch (error) {
      requestFailure(requestId, {
        method: requestOptions.method,
        endpoint,
        error_class: error && error.name,
        message_code: error && error.message
      });
      throw error;
    }
  }

  /**
   * Make a GET request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @param {string} version - API version (v1 or v3)
   * @returns {Promise<Object>} - Response data
   */
  async get(endpoint, options = {}, version = 'v3') {
    return this.request(endpoint, { ...options, method: 'GET' }, version);
  }

  /**
   * Make a POST request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} options - Request options
   * @param {string} version - API version (v1 or v3)
   * @returns {Promise<Object>} - Response data
   */
  async post(endpoint, data, options = {}, version = 'v3') {
    return this.request(
      endpoint,
      { ...options, method: 'POST', body: data },
      version
    );
  }

  /**
   * Make a PUT request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} options - Request options
   * @param {string} version - API version (v1 or v3)
   * @returns {Promise<Object>} - Response data
   */
  async put(endpoint, data, options = {}, version = 'v3') {
    return this.request(
      endpoint,
      { ...options, method: 'PUT', body: data },
      version
    );
  }

  /**
   * Make a DELETE request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @param {string} version - API version (v1 or v3)
   * @returns {Promise<Object>} - Response data
   */
  async delete(endpoint, options = {}, version = 'v3') {
    return this.request(endpoint, { ...options, method: 'DELETE' }, version);
  }
}

// Create a singleton instance
export const apiService = new ApiService();
